import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { CreditCard, FolderOpen, LogOut, Sparkles, User } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../supabase';
import { useIsPro } from '../entitlements';
import { isBillingConfigured, openBillingPortal } from '../billing';

/**
 * Compact signed-in chip in the top menu strip: shows the account and a
 * sign-out action. Renders nothing when auth isn't configured or nobody is
 * signed in, so it stays invisible in the free/offline build.
 */
export function AccountMenu() {
  const [email, setEmail] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pro = useIsPro();

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => setEmail(data.session?.user?.email ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setEmail(s?.user?.email ?? null));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!isSupabaseConfigured || !email) return null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button style={chip} onClick={() => setOpen((o) => !o)} title={email}>
        <User size={13} />
        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {email.split('@')[0]}
        </span>
        <span style={pro ? proBadge : freeBadge}>{pro ? 'PRO' : 'FREE'}</span>
      </button>
      {open && (
        <div style={menu}>
          <div style={menuEmail}>{email}</div>
          <div style={planRow}>
            <span>Plan</span>
            <span style={pro ? proBadge : freeBadge}>{pro ? 'PRO' : 'FREE'}</span>
          </div>
          {!pro && isBillingConfigured && (
            <button
              style={upgradeItem}
              onClick={() => {
                setOpen(false);
                window.dispatchEvent(new CustomEvent('skema:open-upgrade'));
              }}
            >
              <Sparkles size={13} /> Upgrade to Pro
            </button>
          )}
          {pro && isBillingConfigured && (
            <button
              style={menuItem}
              onClick={() => {
                setOpen(false);
                openBillingPortal();
              }}
            >
              <CreditCard size={13} /> Manage subscription
            </button>
          )}
          <button
            style={menuItem}
            onClick={() => {
              setOpen(false);
              window.dispatchEvent(new CustomEvent('skema:open-projects'));
            }}
          >
            <FolderOpen size={13} /> My projects
          </button>
          <div style={menuSep} />
          <button style={menuItem} onClick={() => void supabase?.auth.signOut()}>
            <LogOut size={13} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const chip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text)',
  fontSize: 12,
  fontWeight: 700,
  padding: '5px 9px',
  cursor: 'pointer',
};
const menu: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  right: 0,
  minWidth: 200,
  background: 'var(--panel)',
  border: '1px solid var(--panel-border-strong, var(--panel-border))',
  borderRadius: 'var(--radius)',
  boxShadow: '0 12px 30px rgba(0,0,0,0.3)',
  padding: 6,
  zIndex: 50,
};
const menuEmail: CSSProperties = {
  fontSize: 11.5,
  color: 'var(--text-muted)',
  padding: '6px 8px',
  borderBottom: '1px solid var(--panel-border)',
  marginBottom: 4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const menuItem: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  border: 'none',
  background: 'transparent',
  color: 'var(--text)',
  fontSize: 12.5,
  fontWeight: 600,
  padding: '7px 8px',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  textAlign: 'left',
};
const menuSep: CSSProperties = {
  height: 1,
  background: 'var(--panel-border)',
  margin: '4px 0',
};
const upgradeItem: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  border: '1px solid var(--accent)',
  background: 'var(--accent-soft, transparent)',
  color: 'var(--accent)',
  fontSize: 12.5,
  fontWeight: 800,
  padding: '7px 8px',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  textAlign: 'left',
  margin: '2px 0 6px',
};
const planRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: 11.5,
  color: 'var(--text-muted)',
  padding: '4px 8px 8px',
};
const badgeBase: CSSProperties = {
  fontSize: 9.5,
  fontWeight: 800,
  letterSpacing: 0.6,
  borderRadius: 9,
  padding: '1px 6px',
  lineHeight: '15px',
};
const proBadge: CSSProperties = { ...badgeBase, background: 'var(--accent)', color: '#fff' };
const freeBadge: CSSProperties = {
  ...badgeBase,
  background: 'transparent',
  color: 'var(--text-muted)',
  border: '1px solid var(--panel-border)',
};
